import * as moment from 'moment'
import store from '@/store'
import router from '@/routes'
import * as agencyRoutes from '@/routes/constants/agency'
import * as locationRoutes from '@/routes/constants/location'

function flattenObject(obj: any) {
  let values: string[] = []
  Object.values(obj).forEach((value: any) => {
    if (value && typeof value === 'object' && !Array.isArray(value)) {
      values.push(...flattenObject(value))
    } else {
      values.push(value)
    }
  })
  return values
}
const allAgencyRoutes = flattenObject(agencyRoutes)
const allLocationRoutes = flattenObject(locationRoutes)

export class CustomStorageManager {
  protected prefix: string = 'custom_'
  protected maxSize: number = 5000 // 5KB default

  constructor(config = { prefix: 'custom_', maxSize: 5000 }) {
    this.prefix = config?.prefix
    this.maxSize = config?.maxSize
  }

  protected validateKey(key: string): boolean {
    if (!key || typeof key !== 'string') return false
    if (key.startsWith(this.prefix)) {
      console.warn(`CustomScriptHelper: Keys must be prefixed with "${this.prefix}"`)
      return false
    }
    return true
  }

  private validateValue(value: any): boolean {
    try {
      const serialized = JSON.stringify(value)
      if (serialized.length < 1) {
        console.warn(`CustomScriptHelper: Empty value`)
        return false
      }
      if (serialized.length > this.maxSize) {
        console.warn(`CustomScriptHelper: Value exceeds size limit of ${this.maxSize} bytes`)
        return false
      }
      return true
    } catch (err) {
      console.error('CustomScriptHelper: Something went wrong', err)
      return false
    }
  }

  //local storage
  async getData(key: string): Promise<any> {
    try {
      if (!this.validateKey(key)) return null

      const value = localStorage.getItem(`${this.prefix}${key}`)
      return value ? JSON.parse(value) : null
    } catch (error) {
      console.error('CustomScriptHelper: Error reading from storage:', error)
      return null
    }
  }

  async setData(key: string, value: any): Promise<boolean> {
    try {
      if (!this.validateKey(key)) return false
      if (!this.validateValue(value)) return false

      const prefixedKey = `${this.prefix}${key}`
      localStorage.removeItem(prefixedKey)

      localStorage.setItem(prefixedKey, JSON.stringify(value))
      return true
    } catch (error) {
      console.error('CustomScriptHelper: Error writing to storage:', error)
      return false
    }
  }

  //cookies
  async getCookie(key: string): Promise<any> {
    try {
      if (!this.validateKey(key)) return null
      const prefixedKey = `${this.prefix}${key}`
      const cookies = document.cookie.split(';')
      const cookie = cookies.find(c => c.trim().startsWith(`${prefixedKey}=`))

      if (cookie) {
        const value = cookie.split('=')[1]
        return JSON.parse(value)
      }
    } catch (error) {
      console.error('CustomScriptHelper: Error reading cookie:', error)
    }
    return null
  }

  async setCookie(key: string, value: any, expiry?: number): Promise<boolean> {
    try {
      if (!this.validateKey(key)) return false
      if (!this.validateValue(value)) return false

      const data = JSON.stringify(value)
      const calculatedExpiry = this.getExpiry(expiry)

      document.cookie = `${this.prefix}${key}=${data}; expires=${calculatedExpiry.toUTCString()}; path=/`

      return true
    } catch (error) {
      console.error('CustomScriptHelper: Error writing cookie:', error)
      return false
    }
  }
  private getExpiry(expiry?: number) {
    const MAX_HOURS = 48

    // If no expiry provided, use max hours
    if (!expiry) {
      return moment().add(MAX_HOURS, 'hours').toDate()
    }

    // Handle number input (decimal hours)
    if (typeof expiry === 'number') {
      if (expiry < 0) {
        throw new Error('Expiry must be greater than 0')
      }

      const hours = Math.floor(expiry)
      const minutes = Math.round((expiry - hours) * 60)

      return moment().add(Math.min(hours, MAX_HOURS), 'hours').add(minutes, 'minutes').toDate()
    }
    return moment().add(MAX_HOURS, 'hours').toDate()
  }
}

export class CustomStorageCleanUp extends CustomStorageManager {
  constructor() {
    super()
  }
  async remove(key: string): Promise<boolean> {
    try {
      if (!super.validateKey(key)) return false

      localStorage.removeItem(key)
      return true
    } catch (error) {
      console.error('CustomScriptHelper: Error removing from storage:', error)
      return false
    }
  }

  async keys(): Promise<string[]> {
    return Object.keys(localStorage).filter(key => key.startsWith(this.prefix))
  }

  async clear(): Promise<boolean> {
    try {
      const keys = await this.keys()
      keys.forEach(key => localStorage.removeItem(key))
      await this.clearExpiredCookies()
      return true
    } catch (error) {
      console.error('CustomScriptHelper: Error clearing storage:', error)
      return false
    }
  }

  async clearExpiredCookies(): Promise<boolean> {
    try {
      const now = new Date()

      // Get all cookies
      const cookies = document.cookie.split(';')

      cookies.forEach(cookie => {
        const [key, value] = cookie.trim().split('=')

        // Check if cookie starts with prefix
        if (key.startsWith(this.prefix)) {
          try {
            // Try to parse the value to check expiry
            const parsedValue = JSON.parse(decodeURIComponent(value))

            if (parsedValue.expires && new Date(parsedValue.expires) < now) {
              // Clear expired cookie
              document.cookie = `${key}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;`
            }
          } catch (e) {
            // If parsing fails, consider clearing the cookie
            document.cookie = `${key}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;`
          }
        }
      })

      return true
    } catch (error) {
      console.error('Error clearing expired cookies:', error)
      return false
    }
  }
}

export class Utilities {
  static async getCurrentUser() {
    try {
      //user's data [works with login as feature]
      const user = await store.dispatch('user/get')
      return {
        id: user._id ?? user.id,
        name: user.name ?? `${user.first_name} ${user.last_name}`,
        firstName: user.first_name,
        lastName: user.last_name,
        email: user.email,
        type: user.type,
        role: user.role,
      }
    } catch (err) {
      console.error('Utilities: Something went wrong while getting current user data', err)
    }
  }

  static async getCurrentLocation() {
    try {
      if (router.currentRoute?.params?.location_id) {
        const location = await store.dispatch('locations/getCurrentLocation')
        return {
          id: location.id,
          name: location.name,
          address: {
            address: location.address,
            city: location.city,
            country: location.country,
          },
        }
      } else {
        return {}
      }
    } catch (err) {
      console.error('Utilities: Something went wrong while getting current location data', err)
    }
  }

  static async getCompany() {
    try {
      const company = await store.dispatch('company/get')
      return { id: company._id, name: company.name }
    } catch (err) {
      console.error('Utilities: Something went wrong while getting company data', err)
    }
  }
}

interface INavigationOptions {
  name?: string
  path?: string
  params?: Record<string, string>
  query?: Record<string, string>
  replace?: boolean
  append?: boolean
  // hash?: string;
  // force?: boolean;
}
export class RouteHelper {
  static getCurrentRoute() {
    return router.currentRoute
      ? {
          fullPath: router.currentRoute.fullPath,
          name: router.currentRoute.name,
          params: router.currentRoute.params,
          path: router.currentRoute.path,
          query: router.currentRoute.query,
        }
      : {}
  }

  static async navigate(options: INavigationOptions) {
    try {
      if (options.name) {
        if ([...allAgencyRoutes, ...allLocationRoutes].includes(options.name)) {
          return router.push({
            name: options.name,
            params: options?.params,
            query: options?.query,
            replace: options?.replace,
            append: options?.append,
          })
        } else {
          throw new Error('RouteHelper: Invalid route name')
        }
      } else if (options.path?.length) {
        // Check if path exists in router
        const routeExists = router.getRoutes().some(route => options.path?.match(route.regex))

        if (!routeExists) {
          throw new Error(`RouteHelper: Path "${options.path}" does not exist`)
        }

        return router.push({
          path: options.path,
          params: options?.params,
          query: options?.query,
          replace: options?.replace,
          append: options?.append,
        })
      }

      throw new Error('RouteHelper: Invalid options. Pass a valid name or path')
    } catch (error) {
      console.error('Navigation error:', error)
      return Promise.reject(error)
    }
  }
}
